import React, { useState } from "react";
import { AuthService } from "../auth/authService";
import { EMAIL_REGEX, STRONG_PASSWORD_MESSAGE, STRONG_PASSWORD_REGEX } from "../auth/passwordPolicy";

type Step = "email" | "email_code" | "forgot_email" | "forgot_code" | "forgot_reset";

interface EmailLoginFlowProps {
  onSuccess: () => void;
  // Back to the gettruID / phone push login
  onBack: () => void;
}

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#33ccbf] focus:ring-1 focus:ring-[#33ccbf] disabled:bg-gray-100 disabled:cursor-not-allowed";
const primaryButtonClass =
  "w-full py-2.5 mt-2 text-sm font-semibold text-white bg-[#33ccbf] rounded-lg hover:bg-[#44E3D6] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 active:scale-[0.98]";
const linkClass = "text-xs font-semibold text-[#33ccbf] hover:underline focus:outline-none disabled:opacity-60";

const ErrorBox = ({ message }: { message: string }) =>
  message ? (
    <div className="text-xs text-red-700 bg-red-50 border-l-4 border-red-600 px-3 py-2 rounded-lg leading-relaxed">
      {message}
    </div>
  ) : null;

const InfoBox = ({ message }: { message: string }) =>
  message ? (
    <div className="text-xs text-emerald-800 bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded-lg leading-relaxed">
      {message}
    </div>
  ) : null;

const EmailLoginFlow: React.FC<EmailLoginFlowProps> = ({ onSuccess, onBack }) => {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [secret, setSecret] = useState("");
  const [testCode, setTestCode] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Session held back until the email code is confirmed / the new password is set
  const [pending, setPending] = useState<{ accessToken: string; user: any } | null>(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const goTo = (next: Step) => {
    setStep(next);
    setError("");
    setInfo("");
    setCode("");
  };

  const sendCode = async (purpose: "reverify" | "reset") => {
    const res = await AuthService.requestEmailCode(email, purpose);
    if (res.success && res.secret) {
      setSecret(res.secret);
      setTestCode(res.otp ?? null);
      return true;
    }
    setError(res.error || "Failed to send the code. Please try again.");
    return false;
  };

  // ── Sign in ────────────────────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_REGEX.test(email.trim())) return setError("Enter a valid email address.");
    if (!password) return setError("Enter your password.");
    setLoading(true);
    try {
      const res = await AuthService.emailLogin(email, password);
      if (!res.success || !res.accessToken) {
        setError(res.error || "Invalid email or password.");
        return;
      }
      setPassword("");
      if (!res.isEmailVerificationRequired) {
        await AuthService.saveSession(res.accessToken, res.user);
        onSuccess();
        return;
      }
      // New device or a stale check: confirm the email with a code before entering
      setPending({ accessToken: res.accessToken, user: res.user });
      goTo("email_code");
      if (await sendCode("reverify")) setInfo(`We sent a 6-digit code to ${email.trim()}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignInCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6 || !pending) return;
    setError("");
    setLoading(true);
    try {
      const res = await AuthService.verifyEmailCode(secret, code);
      if (!res.success) {
        setError(res.error || "Invalid code.");
        return;
      }
      await AuthService.saveSession(pending.accessToken, pending.user);
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ────────────────────────────────────────────────────
  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_REGEX.test(email.trim())) return setError("Enter a valid email address.");
    setLoading(true);
    try {
      if (await sendCode("reset")) {
        goTo("forgot_code");
        // Same message whether or not the email exists (the backend doesn't reveal it)
        setInfo("If this email is registered, we've sent a 6-digit code to it.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError("");
    setLoading(true);
    try {
      const verified = await AuthService.verifyEmailCode(secret, code);
      if (!verified.success || !verified.authenticationToken) {
        setError(verified.error || "Invalid code.");
        return;
      }
      const session = await AuthService.loginWithCodeToken(verified.authenticationToken);
      if (!session.success || !session.accessToken) {
        setError(session.error || "Verification failed. Please request a new code.");
        return;
      }
      setPending({ accessToken: session.accessToken, user: session.user });
      setNewPassword("");
      setConfirmPassword("");
      goTo("forgot_reset");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!pending) return goTo("forgot_email");
    if (!STRONG_PASSWORD_REGEX.test(newPassword)) return setError(STRONG_PASSWORD_MESSAGE);
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const res = await AuthService.setPassword(newPassword, pending.accessToken);
      if (!res.success) {
        setError(res.error || "Failed to set the new password.");
        return;
      }
      setNewPassword("");
      setConfirmPassword("");
      // The verified code already gave us a session, so sign straight in
      await AuthService.saveSession(pending.accessToken, pending.user);
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  const resend = async (purpose: "reverify" | "reset") => {
    setError("");
    setInfo("");
    setCode("");
    setLoading(true);
    try {
      if (await sendCode(purpose)) setInfo("A new code is on its way.");
    } finally {
      setLoading(false);
    }
  };

  // ── Shared pieces ──────────────────────────────────────────────────────
  const codeField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="email-code-input" className="text-xs font-semibold text-gray-700">
        6-digit code
      </label>
      <input
        id="email-code-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        disabled={loading}
        className={`${inputClass} text-center text-lg font-bold tracking-[0.5em]`}
      />
      {testCode && (
        <p className="text-[10px] text-gray-500 mt-1">
          Test account code: <span className="font-mono font-bold text-gray-700">{testCode}</span>
        </p>
      )}
    </div>
  );

  const passwordPairFields = (
    <>
      <div className="flex flex-col gap-1">
        <label htmlFor="new-password-input" className="text-xs font-semibold text-gray-700">
          New password
        </label>
        <input
          id="new-password-input"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
          className={inputClass}
        />
        <p className="text-[10px] text-gray-500 mt-1 leading-normal">{STRONG_PASSWORD_MESSAGE}</p>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="confirm-password-input" className="text-xs font-semibold text-gray-700">
          Confirm new password
        </label>
        <input
          id="confirm-password-input"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          className={inputClass}
        />
      </div>
    </>
  );

  // ── Screens ────────────────────────────────────────────────────────────
  if (step === "email_code") {
    return (
      <div>
        <h2 className="text-base font-bold text-gray-800 mb-1">Check your email</h2>
        <p className="text-xs text-gray-500 mb-5">Enter the code we sent to confirm it's you.</p>
        <form onSubmit={handleVerifySignInCode} className="flex flex-col gap-4">
          <InfoBox message={info} />
          {codeField}
          <ErrorBox message={error} />
          <button type="submit" disabled={loading || code.length < 6} className={primaryButtonClass}>
            {loading ? "Verifying..." : "Verify and sign in"}
          </button>
        </form>
        <div className="mt-6 flex items-center justify-between">
          <button type="button" onClick={() => { setPending(null); goTo("email"); }} className={linkClass}>
            Back
          </button>
          <button type="button" disabled={loading} onClick={() => resend("reverify")} className={linkClass}>
            Resend code
          </button>
        </div>
      </div>
    );
  }

  if (step === "forgot_email") {
    return (
      <div>
        <h2 className="text-base font-bold text-gray-800 mb-1">Reset your password</h2>
        <p className="text-xs text-gray-500 mb-5">We'll email you a code to set a new password.</p>
        <form onSubmit={handleForgotRequest} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="forgot-email-input" className="text-xs font-semibold text-gray-700">
              Email
            </label>
            <input
              id="forgot-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className={inputClass}
            />
          </div>
          <ErrorBox message={error} />
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? "Sending code..." : "Send code"}
          </button>
        </form>
        <div className="mt-6 text-center">
          <button type="button" onClick={() => goTo("email")} className={linkClass}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (step === "forgot_code") {
    return (
      <div>
        <h2 className="text-base font-bold text-gray-800 mb-1">Enter the code</h2>
        <p className="text-xs text-gray-500 mb-5">Check {email.trim()} for a 6-digit code.</p>
        <form onSubmit={handleForgotVerify} className="flex flex-col gap-4">
          <InfoBox message={info} />
          {codeField}
          <ErrorBox message={error} />
          <button type="submit" disabled={loading || code.length < 6} className={primaryButtonClass}>
            {loading ? "Verifying..." : "Continue"}
          </button>
        </form>
        <div className="mt-6 flex items-center justify-between">
          <button type="button" onClick={() => goTo("forgot_email")} className={linkClass}>
            Back
          </button>
          <button type="button" disabled={loading} onClick={() => resend("reset")} className={linkClass}>
            Resend code
          </button>
        </div>
      </div>
    );
  }

  if (step === "forgot_reset") {
    return (
      <div>
        <h2 className="text-base font-bold text-gray-800 mb-1">Set a new password</h2>
        <p className="text-xs text-gray-500 mb-5">You'll be signed in once it's saved.</p>
        <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
          {passwordPairFields}
          <ErrorBox message={error} />
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? "Saving..." : "Save and sign in"}
          </button>
        </form>
      </div>
    );
  }

  // step === "email"
  return (
    <div>
      <h2 className="text-base font-bold text-gray-800 mb-1">Sign in with email</h2>
      <p className="text-xs text-gray-500 mb-5">Use the email and password of your gettru account.</p>
      <form onSubmit={handleSignIn} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="email-input" className="text-xs font-semibold text-gray-700">
            Email
          </label>
          <input
            id="email-input"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label htmlFor="password-input" className="text-xs font-semibold text-gray-700">
              Password
            </label>
            <button type="button" onClick={() => goTo("forgot_email")} className="text-[11px] font-semibold text-[#33ccbf] hover:underline">
              Forgot password?
            </button>
          </div>
          <input
            id="password-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className={inputClass}
          />
        </div>
        <ErrorBox message={error} />
        <button type="submit" disabled={loading} className={primaryButtonClass}>
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
      <div className="mt-6 text-center">
        <button type="button" onClick={onBack} className={linkClass}>
          Sign in with gettruID or phone instead
        </button>
      </div>
    </div>
  );
};

export default EmailLoginFlow;
